import { AnimatedHeading, AnimatedHeadingText } from './AnimatedHeading'
import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import type { Locale } from '../content/site'
import { ArrowIcon } from './Icons'
import { LeadApiError, sendLead, normalizeIranianMobile, type ProjectLead } from '../services/leads'

type Stage = 'phone' | 'details' | 'success'
type Contact = 'call' | 'whatsapp' | 'telegram'
const services = [
  { value: 'website', fa: 'طراحی وب‌سایت', en: 'Website design' },
  { value: 'shop', fa: 'فروشگاه اینترنتی', en: 'Online store' },
  { value: 'mobile', fa: 'اپلیکیشن موبایل', en: 'Mobile app' },
  { value: 'software', fa: 'نرم‌افزار اختصاصی', en: 'Custom software' },
  { value: 'identity', fa: 'هویت بصری و برندینگ', en: 'Brand identity' },
  { value: 'seo', fa: 'سئو و رشد دیجیتال', en: 'SEO & digital growth' },
  { value: 'ai', fa: 'هوش مصنوعی و اتوماسیون', en: 'AI & automation' },
  { value: 'consultation', fa: 'مشاوره تخصصی', en: 'Consulting' },
  { value: 'unsure', fa: 'هنوز مطمئن نیستم', en: 'Not sure yet' },
] as const
const budgets = [
  { value: 'under-30m', fa: 'زیر 30 میلیون تومان', en: 'Under 30M toman' },
  { value: '30-80m', fa: '30 تا 80 میلیون تومان', en: '30–80M toman' },
  { value: '80-150m', fa: '80 تا 150 میلیون تومان', en: '80–150M toman' },
  { value: 'over-150m', fa: 'بیش از 150 میلیون تومان', en: 'Over 150M toman' },
  { value: 'undecided', fa: 'هنوز مشخص نیست', en: 'Not decided yet' },
] as const
const starts = [
  { value: 'asap', fa: 'در اولین فرصت', en: 'As soon as possible' },
  { value: 'one-month', fa: 'تا یک ماه آینده', en: 'Within a month' },
  { value: 'three-months', fa: '1 تا 3 ماه آینده', en: 'In 1–3 months' },
  { value: 'planning', fa: 'در حال بررسی هستم', en: 'Still planning' },
] as const

const copy = {
  fa: {
    eyebrow: 'پایه‌تک / آغاز یک همکاری',
    titleA: 'ایده شما از اینجا',
    titleB: 'واقعی می‌شود.',
    intro: 'از یک وب‌سایت حرفه‌ای تا محصول نرم‌افزاری اختصاصی؛ شماره‌ات را بگذار تا درباره بهترین مسیر اجرای پروژه حرف بزنیم.',
    promise: 'پاسخ‌گویی در کمتر از 24 ساعت',
    promiseNote: 'هدف ما تماس در کمتر از 24 ساعت است؛ زمان دقیق بسته به ساعت و شرایط کاری ممکن است متفاوت باشد.',
    trustTitle: 'چه اتفاقی بعد از ثبت درخواست می‌افتد؟',
    trustSteps: [['01','ثبت درخواست','فقط شماره تماس کافی است.'],['02','شناخت نیاز','در تماس اولیه درباره مسئله و اهداف صحبت می‌کنیم.'],['03','پیشنهاد همکاری','مسیر اجرا، محدوده کار و برآورد را شفاف می‌کنیم.']],
    highlights: ['گفت‌وگوی مستقیم با تیم فنی','مسیر اجرای شفاف','طراحی متناسب با کسب‌وکار'],
    quick: 'راه سریع — فقط شماره',
    stepCount: 'مرحله اول از دو',
    nextStep: 'می‌خواهم جزئیات پروژه را هم اضافه کنم',
    details: 'جزئیات اختیاری پروژه',
    detailsNote: 'هیچ‌کدام از فیلدهای این بخش الزامی نیستند. همین حالا هم می‌توانی فقط با شماره درخواست بدهی.',
    phone: 'شماره موبایل',
    phoneExample: 'مثلاً 09121234567',
    phoneError: 'شماره موبایل معتبر ایرانی وارد کنید (11 رقم و شروع با 09).',
    consent: 'با تماس تیم پایه‌تک درباره درخواست خودم موافقم.',
    consentError: 'برای ثبت درخواست، رضایت تماس را تأیید کنید.',
    sendQuick: 'ثبت درخواست تماس',
    continue: 'تکمیل فرم پیشرفته (اختیاری)',
    back: 'بازگشت به شماره',
    submit: 'ارسال درخواست همکاری',
    name: 'نام و نام خانوادگی',
    business: 'نام کسب‌وکار / برند',
    service: 'چه خدماتی لازم داری؟',
    serviceBlank: 'انتخاب خدمت (اختیاری)',
    description: 'کمی درباره پروژه بگو',
    descriptionHint: 'هدف پروژه، امکانات مهم و مسئله‌ای که می‌خواهی حل شود…',
    budget: 'حدود بودجه',
    budgetBlank: 'بازه بودجه (اختیاری)',
    timeline: 'زمان مدنظر برای شروع',
    timelineBlank: 'زمان شروع (اختیاری)',
    contact: 'روش تماس ترجیحی',
    links: 'لینک نمونه یا فایل مرجع',
    linksHint: 'https://…',
    attachment: 'پیوست اختیاری (حداکثر 5 مگابایت)',
    call: 'تماس تلفنی',
    whatsapp: 'واتساپ',
    telegram: 'تلگرام',
    sendError: 'درخواست ارسال نشد. لطفاً دوباره تلاش کنید یا مستقیم تماس بگیرید.',
    unavailable: 'سرویس ثبت درخواست هنوز متصل نشده است. شماره‌ات ذخیره نشد؛ فعلاً از تماس مستقیم استفاده کن.',
    busy: 'در حال ثبت درخواست…',
    successTitle: 'درخواستت دریافت شد',
    successBody: 'اطلاعاتت با موفقیت به سامانه ثبت درخواست منتقل شد. تیم پایه‌تک برای بررسی و هماهنگی تماس می‌گیرد.',
    again: 'ثبت درخواست دیگر',
    home: 'بازگشت به صفحه اصلی',
    or: 'یا مستقیم تماس بگیر',
    privateNote: 'شماره‌ات فقط برای پیگیری همین درخواست استفاده خواهد شد. اطلاعات فرم اختیاری است.',
    maxSize: 'حجم فایل باید کمتر از 5 مگابایت باشد.',
    kindError: 'نوع فایل مجاز نیست. PDF، تصویر یا فایل متنی انتخاب کنید.',
  },
  en: {
    eyebrow: 'Paye Tech / Start your project',
    titleA: 'Your next big idea',
    titleB: 'starts here.',
    intro: 'From a professional website to a custom software product. Leave your number and we’ll help you find the right approach.',
    promise: 'We aim to respond within 24 hours',
    promiseNote: 'Our target is a reply within 24 hours. Exact timing depends on working hours and availability.',
    trustTitle: 'What happens next?',
    trustSteps: [['01','Send your request','Your phone number is enough.'],['02','Discuss your needs','We explore your goals and requirements.'],['03','Shape a proposal','We outline the scope, approach and estimate.']],
    highlights: ['Direct access to a technical team','Clear delivery approach','Tailored to your business'],
    quick: 'Quick request — just your number',
    stepCount: 'Step one of two',
    nextStep: 'Add project details',
    details: 'Optional project brief',
    detailsNote: 'Every field in this step is optional. You can still submit with only your phone number.',
    phone: 'Mobile phone number',
    phoneExample: 'e.g. 09121234567',
    phoneError: 'Enter a valid Iranian mobile number, starting with 09.',
    consent: 'I agree to be contacted by Paye Tech about this request.',
    consentError: 'Please consent to being contacted to submit your request.',
    sendQuick: 'Request a callback',
    continue: 'Complete optional details',
    back: 'Back to phone',
    submit: 'Send project request',
    name: 'Full name',
    business: 'Company or brand name',
    service: 'What service do you need?',
    serviceBlank: 'Choose a service (optional)',
    description: 'Tell us about the project',
    descriptionHint: 'Describe the goal, key features or problem you want to solve…',
    budget: 'Estimated budget',
    budgetBlank: 'Budget range (optional)',
    timeline: 'Target start date',
    timelineBlank: 'Start window (optional)',
    contact: 'Preferred contact method',
    links: 'Reference link',
    linksHint: 'https://…',
    attachment: 'Optional attachment (max 5 MB)',
    call: 'Phone call',
    whatsapp: 'WhatsApp',
    telegram: 'Telegram',
    sendError: 'We could not send your request. Please retry or call us directly.',
    unavailable: 'The request service is not connected yet. Your number was not saved; please contact us directly.',
    busy: 'Sending your request…',
    successTitle: 'Request received',
    successBody: 'Your details have been accepted by our inquiry service. Our team will reach out to discuss next steps.',
    again: 'Start another request',
    home: 'Back to homepage',
    or: 'Or call directly',
    privateNote: 'We use your phone number only to follow up on this request. Additional details are optional.',
    maxSize: 'The file must be smaller than 5 MB.',
    kindError: 'Please select a PDF, image or text document.',
  },
} as const

const initialDetails = {
  name: '', business: '', service: '', description: '', budget: '', timeline: '',
  preferredContact: 'call' as Contact, referenceUrl: '',
}
const allowedMime = ['application/pdf','image/png','image/jpeg','image/webp','text/plain']
const MAX_UPLOAD = 5 * 1024 * 1024

export function StartProjectPage({ locale }: { locale: Locale }) {
  const t = copy[locale]
  const [stage, setStage] = useState<Stage>('phone')
  const [mobile, setMobile] = useState('')
  const [consent, setConsent] = useState(false)
  const [details, setDetails] = useState(initialDetails)
  const [attachment, setAttachment] = useState<File | null>(null)
  const [invalid, setInvalid] = useState('')
  const [status, setStatus] = useState('')
  const [sending, setSending] = useState(false)
  const [trap, setTrap] = useState('')
  const [requestId, setRequestId] = useState('')

  useEffect(() => { document.title = locale === 'fa' ? 'شروع همکاری | پایه‌تک' : 'Start a project | Paye Tech' }, [locale])
  const clearErrors = () => { setInvalid(''); setStatus('') }
  const validate = () => {
    if (!normalizeIranianMobile(mobile)) { setInvalid('phone'); return false }
    if (!consent) { setInvalid('consent'); return false }
    setInvalid('')
    return true
  }
  const next = () => { clearErrors(); if (validate()) { setStage('details'); window.scrollTo({top:0,behavior:'smooth'}) } }
  const update = (name: keyof typeof initialDetails, value: string) => { setDetails(current => ({...current,[name]:value}));setStatus('') }
  const onFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null
    if (!file) { setAttachment(null); return }
    if (file.size > MAX_UPLOAD) { setInvalid('file-size');setAttachment(null);event.target.value='';return }
    if (!allowedMime.includes(file.type)) { setInvalid('file-kind');setAttachment(null);event.target.value='';return }
    setAttachment(file);clearErrors()
  }
  const submit = async (event: FormEvent<HTMLFormElement>, mode: ProjectLead['mode']) => {
    event.preventDefault()
    if (sending || !validate()) return
    setStatus('');setSending(true)
    const id = requestId || (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : String(Date.now()))
    setRequestId(id)
    try {
      await sendLead({
        phone: normalizeIranianMobile(mobile)!, mode, consent: true,
        source: window.location.pathname, ...mode === 'detailed' ? details : initialDetails,
        honeypot: trap, idempotencyKey: id, attachment: mode === 'detailed' ? attachment : null,
      })
      setStage('success')
    } catch (error) {
      setStatus(error instanceof LeadApiError && error.code === 'not-configured' ? 'unavailable' : 'sendError')
    } finally { setSending(false) }
  }
  return (
    <div className="start-project-page">
      <div className="start-project-page__glow start-project-page__glow--one" aria-hidden="true" />
      <div className="start-project-page__glow start-project-page__glow--two" aria-hidden="true" />
      <div className="start-project-page__grid" aria-hidden="true" />
      <div className="start-project-page__inner">
        <header className="start-project-page__intro">
          <span className="start-project-page__eyebrow"><span className="start-project-page__pulse" />{t.eyebrow}</span>
          <h1><AnimatedHeadingText>{t.titleA}</AnimatedHeadingText><AnimatedHeadingText className="start-project-page__headline-accent">{t.titleB}</AnimatedHeadingText></h1>
          <p>{t.intro}</p>
          <div className="start-project-page__promise"><span aria-hidden="true">◷</span><strong>{t.promise}</strong></div>
          <p className="start-project-page__promise-note">{t.promiseNote}</p>
          <ul className="start-project-page__highlights">{t.highlights.map(item=><li key={item}><span aria-hidden="true">✓</span>{item}</li>)}</ul>
        </header>
        <section className="start-project-page__panel" aria-label={stage === 'details' ? t.details : t.quick}>
          <div className="start-project-page__panel-rim" aria-hidden="true" />
          {stage === 'success' ? (
            <div className="start-project-page__complete" role="status">
              <div className="start-project-page__complete-icon" aria-hidden="true">✓</div>
              <AnimatedHeading as="h2">{t.successTitle}</AnimatedHeading><p>{t.successBody}</p>
              <button type="button" className="start-project-page__primary" onClick={() => {setStage('phone');setMobile('');setConsent(false);setDetails(initialDetails);setRequestId('');setAttachment(null);clearErrors()}}>{t.again}<ArrowIcon /></button>
              <Link to="/" className="start-project-page__secondary">{t.home}</Link>
            </div>
          ) : (
            <form noValidate onSubmit={(event) => { void submit(event,stage === 'details' ? 'detailed' : 'quick') }}>
              <div className="start-project-page__form-head">
                <span>{stage === 'phone' ? t.stepCount : t.details}</span>
                <strong dir="ltr">{stage === 'phone' ? '01 / 02' : '02 / 02'}</strong>
              </div>
              <AnimatedHeading as="h2">{stage === 'phone' ? t.quick : t.details}</AnimatedHeading>
              {stage === 'details' && <p className="start-project-page__subtext">{t.detailsNote}</p>}
              <label className="start-project-page__field start-project-page__phone-field">
                <span>{t.phone} <span aria-hidden="true">*</span></span>
                <input type="tel" name="phone" autoComplete="tel-national" inputMode="tel" dir="ltr" maxLength={18} placeholder={t.phoneExample} value={mobile} onChange={event=>{setMobile(event.target.value);clearErrors()}} required aria-invalid={invalid === 'phone'} aria-describedby={invalid === 'phone' ? 'sp-mobile-error' : undefined} />
              </label>
              {invalid === 'phone' && <p id="sp-mobile-error" className="start-project-page__error" role="alert">{t.phoneError}</p>}
              {stage === 'details' && (
                <div className="start-project-page__advanced">
                  <div className="start-project-page__fields">
                    <label className="start-project-page__field"><span>{t.name}</span><input type="text" value={details.name} maxLength={100} autoComplete="name" onChange={e=>update('name',e.target.value)} /></label>
                    <label className="start-project-page__field"><span>{t.business}</span><input type="text" value={details.business} maxLength={140} onChange={e=>update('business',e.target.value)} /></label>
                    <label className="start-project-page__field"><span>{t.service}</span><select value={details.service} onChange={e=>update('service',e.target.value)}><option value="">{t.serviceBlank}</option>{services.map(s=><option key={s.value} value={s.value}>{s[locale]}</option>)}</select></label>
                    <label className="start-project-page__field"><span>{t.budget}</span><select value={details.budget} onChange={e=>update('budget',e.target.value)}><option value="">{t.budgetBlank}</option>{budgets.map(b=><option key={b.value} value={b.value}>{b[locale]}</option>)}</select></label>
                    <label className="start-project-page__field"><span>{t.timeline}</span><select value={details.timeline} onChange={e=>update('timeline',e.target.value)}><option value="">{t.timelineBlank}</option>{starts.map(s=><option key={s.value} value={s.value}>{s[locale]}</option>)}</select></label>
                    <label className="start-project-page__field"><span>{t.contact}</span><select value={details.preferredContact} onChange={e=>update('preferredContact',e.target.value as Contact)}><option value="call">{t.call}</option><option value="whatsapp">{t.whatsapp}</option><option value="telegram">{t.telegram}</option></select></label>
                    <label className="start-project-page__field start-project-page__field--wide"><span>{t.description}</span><textarea maxLength={4000} rows={4} placeholder={t.descriptionHint} value={details.description} onChange={e=>update('description',e.target.value)} /></label>
                    <label className="start-project-page__field"><span>{t.links}</span><input type="url" value={details.referenceUrl} placeholder={t.linksHint} maxLength={500} dir="ltr" onChange={e=>update('referenceUrl',e.target.value)} /></label>
                    <label className="start-project-page__field start-project-page__file"><span>{t.attachment}</span><input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.txt" onChange={onFile} /></label>
                  </div>
                </div>
              )}
              <div className="start-project-page__hidden" aria-hidden="true"><label>Leave this field empty<input tabIndex={-1} autoComplete="off" value={trap} onChange={e=>setTrap(e.target.value)} /></label></div>
              <label className="start-project-page__consent"><input type="checkbox" checked={consent} onChange={e=>{setConsent(e.target.checked);clearErrors()}} /><span>{t.consent}</span></label>
              {invalid === 'consent' && <p className="start-project-page__error" role="alert">{t.consentError}</p>}
              {invalid === 'file-size' && <p className="start-project-page__error" role="alert">{t.maxSize}</p>}
              {invalid === 'file-kind' && <p className="start-project-page__error" role="alert">{t.kindError}</p>}
              {status && <div className="start-project-page__error start-project-page__submit-error" role="alert">{status === 'unavailable' ? t.unavailable : t.sendError}</div>}
              <button disabled={sending} type="submit" className="start-project-page__primary">{sending ? t.busy : stage === 'phone' ? t.sendQuick : t.submit}<ArrowIcon /></button>
              {stage === 'phone' ? <button type="button" onClick={next} disabled={sending} className="start-project-page__secondary">{t.continue}<span aria-hidden="true">＋</span></button> : <button type="button" onClick={() => {setStage('phone');clearErrors()}} disabled={sending} className="start-project-page__secondary">{t.back}</button>}
              <p className="start-project-page__privacy">{t.privateNote}</p>
            </form>
          )}
        </section>
        <section className="start-project-page__how" aria-labelledby="sp-how-title"><AnimatedHeading as="h2" id="sp-how-title">{t.trustTitle}</AnimatedHeading><ol>{t.trustSteps.map(([number,title,body])=><li key={number}><span>{number}</span><strong>{title}</strong><p>{body}</p></li>)}</ol><div className="start-project-page__direct"><span>{t.or}</span><a href="tel:+982636631550" dir="ltr">02636631550</a></div></section>
      </div>
    </div>
  )
}
